<script setup lang="ts">
import QRCode from 'qrcode'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import BrandLogo from '../components/BrandLogo.vue'
import { ApiError, apiRequest } from '../services/api'
import { optionLetter } from '../services/bluff'
import type { ScreenBluffView } from '../../shared/contracts/bluff'

interface ScreenState {
  session: { id: string; joinCode: string; name: string; phase: string; status: string; revision: number; closeAt: string | null; mode?: 'BLUFF' }
  theme: Record<string, string | null>
  question: { id: string; prompt: string; image_url: string | null; type: string; choices: Array<{ id: string; label?: string; imageUrl?: string }> | null } | null
  progress: { answered: number; eligible: number }
  revealGroups: Array<{ canonical_answer: string; rank: number; final_count: number }>
  standings: Array<{ id: string; name: string; score: number; rank: number }>
  playerCount: number
  bluff?: ScreenBluffView
}

const sessionId = String(useRoute().params.sessionId)
const joinHost = location.host
const state = ref<ScreenState | null>(null)
const error = ref('')
const connected = ref(false)
const qrCode = ref('')
const now = ref(Date.now())
let socket: WebSocket | null = null
let reconnectTimer: number | undefined
let clockTimer: number | undefined
let stopped = false

const secondsRemaining = computed(() => state.value?.session.closeAt
  ? Math.max(0, Math.ceil((Date.parse(state.value.session.closeAt) - now.value) / 1000)) : null)
const isBluff = computed(() => state.value?.session.mode === 'BLUFF')
const themeStyle = computed(() => {
  const theme = state.value?.theme ?? {}
  return {
    '--background': theme.background ?? '#1d1e24', '--primary': theme.primary ?? '#9cecfe',
    '--secondary': theme.secondary ?? '#2a2d33', '--accent': theme.accent ?? '#ffba23',
    '--text': theme.text ?? '#ffffff', '--card': theme.cardBackground ?? '#2a2d33',
    ...(theme.backgroundUrl ? { backgroundImage: `linear-gradient(#0008, #0008), url(${theme.backgroundUrl})` } : {}),
  }
})

async function refresh(): Promise<void> {
  try {
    state.value = await apiRequest<ScreenState>(`/api/screen/${sessionId}`)
    qrCode.value = await QRCode.toDataURL(`${location.origin}/join/${state.value.session.joinCode}`, { width: 420, margin: 1 })
    error.value = ''
  } catch (caught) { error.value = caught instanceof ApiError ? caught.message : 'Unable to load the Big Screen.' }
}

function connect(): void {
  if (stopped) return
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
  socket = new WebSocket(`${protocol}//${location.host}/api/screen/${sessionId}/socket`, ['bar-bluff'])
  socket.addEventListener('open', () => { connected.value = true })
  socket.addEventListener('message', () => { void refresh() })
  socket.addEventListener('close', () => {
    connected.value = false
    if (!stopped) reconnectTimer = window.setTimeout(connect, 1500)
  })
}

onMounted(async () => {
  await refresh()
  connect()
  clockTimer = window.setInterval(() => { now.value = Date.now() }, 250)
})
onBeforeUnmount(() => {
  stopped = true
  if (reconnectTimer) window.clearTimeout(reconnectTimer)
  if (clockTimer) window.clearInterval(clockTimer)
  socket?.close(1000, 'Leaving screen')
})
</script>

<template>
  <main
    class="big-screen"
    :style="themeStyle"
  >
    <p
      v-if="error"
      class="screen-message"
      role="alert"
    >
      {{ error }}
    </p>
    <template v-else-if="state">
      <header class="screen-header">
        <img
          v-if="state.theme.logoUrl"
          :src="state.theme.logoUrl"
          alt=""
        >
        <BrandLogo
          v-else
          large
        />
        <strong>{{ state.session.name }}</strong>
        <span
          class="live"
          :class="{ offline: !connected }"
        >{{ connected ? 'Live' : 'Reconnecting…' }}</span>
      </header>

      <section
        v-if="state.session.phase === 'LOBBY'"
        class="screen-lobby"
      >
        <div>
          <p class="screen-kicker">
            <strong>Join</strong> the game
          </p>
          <h1>{{ state.session.joinCode }}</h1>
          <p>{{ state.playerCount }} players joined</p>
          <p class="mt-16">
            Go to <strong>{{ joinHost }}</strong> and enter the Game ID, or scan the code.
          </p>
        </div>
        <img
          v-if="qrCode"
          :src="qrCode"
          :alt="`QR code to join game ${state.session.joinCode}`"
        >
      </section>

      <section
        v-else-if="isBluff && state.bluff && ['BLUFF_OPEN', 'REVIEW', 'VOTE_OPEN'].includes(state.session.phase)"
        class="screen-center"
      >
        <p class="screen-kicker">
          <template v-if="state.session.phase === 'BLUFF_OPEN'">
            <strong>Bluff</strong>now
          </template>
          <template v-else-if="state.session.phase === 'REVIEW'">
            <strong>Bluffs</strong>closed
          </template>
          <template v-else>
            <strong>Find</strong>the truth
          </template>
        </p>
        <p class="mt-16">
          Question {{ state.bluff.questionNumber }} of {{ state.bluff.questionCount }}
        </p>
        <h1 class="mt-16">
          {{ state.question?.prompt }}
        </h1>
        <ol
          v-if="state.session.phase === 'VOTE_OPEN'"
          class="screen-options"
        >
          <li
            v-for="(option, index) in state.bluff.options"
            :key="option.id"
          >
            <strong><span class="rank">{{ optionLetter(index) }}</span>{{ option.text }}</strong>
          </li>
        </ol>
        <strong
          v-if="secondsRemaining !== null && !state.bluff.votingClosed && state.session.phase !== 'REVIEW'"
          class="screen-timer"
        >{{ secondsRemaining }}</strong>
        <p class="mt-16">
          <template v-if="state.session.phase === 'BLUFF_OPEN'">
            {{ state.bluff.submitted }} of {{ state.bluff.eligible }} teams have a bluff in. Captains: make it believable.
          </template>
          <template v-else-if="state.session.phase === 'REVIEW'">
            The host is mixing the lies with the truth…
          </template>
          <template v-else-if="state.bluff.votingClosed">
            Voting is closed.
          </template>
          <template v-else>
            {{ state.bluff.voted }} of {{ state.bluff.eligible }} teams voted. You can't vote for your own bluff.
          </template>
        </p>
      </section>

      <section
        v-else-if="isBluff && state.session.phase === 'REVEAL' && state.bluff?.reveal"
        class="screen-results"
      >
        <p class="screen-kicker">
          <strong>The truth</strong>is out
        </p>
        <h1 class="mt-16">
          {{ state.question?.prompt }}
        </h1>
        <p class="screen-explanation">
          {{ state.bluff.reveal.explanation }} <span class="text-sm">(Source: {{ state.bluff.reveal.sourceName }})</span>
        </p>
        <ol class="screen-options">
          <li
            v-for="(option, index) in state.bluff.reveal.options"
            :key="option.id"
            :class="{ 'is-truth': option.kind === 'TRUTH' }"
          >
            <strong><span class="rank">{{ optionLetter(index) }}</span>{{ option.text }}</strong>
            <small>
              {{ option.kind === 'TRUTH' ? 'THE TRUTH' : option.kind === 'DECOY' ? 'House decoy' : `Bluff by ${option.authors.join(', ')}` }}
              · {{ option.voters.length ? `${option.kind === 'TRUTH' ? 'Found by' : 'Fooled'} ${option.voters.join(', ')}` : 'no votes' }}
              <template v-if="option.points"> · +{{ option.points }}</template>
            </small>
          </li>
        </ol>
      </section>

      <section
        v-else-if="!isBluff && ['QUESTION_OPEN', 'QUESTION_CLOSED', 'REVIEW'].includes(state.session.phase)"
        class="screen-center"
      >
        <p class="screen-kicker">
          <strong>{{ state.session.phase === 'QUESTION_OPEN' ? 'Answer' : 'Answers' }}</strong>{{ state.session.phase === 'QUESTION_OPEN' ? 'now' : 'closed' }}
        </p>
        <h1 class="mt-16">
          {{ state.question?.prompt }}
        </h1>
        <img
          v-if="state.question?.image_url"
          class="question-image mt-16"
          :src="state.question.image_url"
          alt=""
        >
        <strong
          v-if="secondsRemaining !== null"
          class="screen-timer"
        >{{ secondsRemaining }}</strong>
        <p class="mt-16">
          {{ state.progress.answered }} of {{ state.progress.eligible }} teams answered
        </p>
      </section>

      <section
        v-else-if="state.session.phase === 'REVEALED'"
        class="screen-results"
      >
        <p class="screen-kicker">
          <strong>The crowd</strong> has spoken
        </p>
        <h1 class="mt-16">
          {{ state.question?.prompt }}
        </h1>
        <ol>
          <li
            v-for="group in state.revealGroups"
            :key="`${group.rank}-${group.canonical_answer}`"
          >
            <strong><span class="rank">{{ group.rank }}</span>#{{ group.rank }} {{ group.canonical_answer }}</strong><span>{{ group.final_count }} teams</span>
          </li>
        </ol>
      </section>

      <section
        v-else-if="state.session.phase === 'STANDINGS'"
        class="screen-results"
      >
        <p class="screen-kicker">
          <strong>Leaderboard</strong>
        </p>
        <h1 class="mt-16">
          Standings
        </h1>
        <ol>
          <li
            v-for="team in state.standings"
            :key="team.id"
          >
            <strong><span class="rank">{{ team.rank }}</span>#{{ team.rank }} {{ team.name }}</strong><span>{{ team.score }}</span>
          </li>
        </ol>
      </section>

      <section
        v-else-if="state.session.phase === 'COMPLETE'"
        class="screen-results"
      >
        <p class="screen-kicker">
          <strong>Final</strong> results
        </p>
        <h1 class="mt-16">
          Podium
        </h1>
        <div class="podium">
          <article
            v-for="team in state.standings.slice(0, 3)"
            :key="team.id"
          >
            <span>#{{ team.rank }}</span><strong>{{ team.name }}</strong><b>{{ team.score }}</b>
          </article>
        </div>
        <ol>
          <li
            v-for="team in state.standings"
            :key="team.id"
          >
            <strong><span class="rank">{{ team.rank }}</span>#{{ team.rank }} {{ team.name }}</strong><span>{{ team.score }}</span>
          </li>
        </ol>
      </section>

      <section
        v-else
        class="screen-center"
      >
        <h1>Session ended</h1><p class="mt-16">
          Thanks for playing Bar Bluff.
        </p>
      </section>
    </template>
  </main>
</template>
